import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from "@sentry/react";
import { supabase } from './lib/supabase';
import { initSessionVault } from './lib/session-vault';
import { isTransientNetworkError } from './lib/supabase-error-handler';
import { isProductionSupabase } from './lib/env';
import { isStaleChunkError } from './lib/stale-chunk-error';
import { isInjectedEvalError } from './lib/injected-eval-error';
import App from './App.tsx';
import './index.css';

// Start the admin PIN fast-switch session vault. No-op unless this device has
// opted in (PIN_SWITCH_DEVICE_FLAG_KEY), so ordinary devices never capture
// session snapshots. See docs/ADMIN_PIN_LOGIN_SPEC.md.
initSessionVault();
import { Button } from './components/ui/button';

// Handle expired refresh tokens from Supabase's background auto-refresh timer.
// When a tab is left open overnight the refresh token expires server-side and the
// SDK throws an unhandled AuthApiError. Catch it here, stop the timer, and clear
// the local session so the user is redirected to the login page.
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const isAuthRefreshError =
    // Expired/revoked refresh token
    (error?.name === 'AuthApiError' && error?.message?.includes('Refresh Token')) ||
    // Network failure during token refresh (Safari "Load failed")
    (error?.name === 'AuthRetryableFetchError') ||
    // Session lost after Safari background tab throttling kills auto-refresh
    (error?.name === 'AuthSessionMissingError');

  if (isAuthRefreshError) {
    event.preventDefault(); // suppress console noise
    supabase.auth.stopAutoRefresh();
    supabase.auth.signOut({ scope: 'local' });
  }
});

// Initialize Sentry only if DSN is available and not blocked by ad blockers
const SENTRY_DSN = "https://eb68c39dce17259d81612716affb1f8f@o4509561165053952.ingest.de.sentry.io/4509567090557008";

// Transient/ad-blocker network signatures that should never reach Sentry.
// Single source of truth for the beforeSend filter — previously this string
// list was duplicated across the hint, exception-chain, and extra-error checks.
// Delegates to the shared isTransientNetworkError() classifier (Safari
// "Load failed", Supabase StorageUnknownError/DOMException) and adds the
// Sentry-only noise we filter here but deliberately keep out of the
// hook-facing classifier: ad-blocker aborts and the Edge Function fetch wrapper.
const SENTRY_NETWORK_NOISE = [
  'ERR_BLOCKED_BY_CLIENT',
  'Failed to fetch',
  'NetworkError',
  'Load failed',
  'Failed to send a request to the Edge Function',
  'A network error occurred. Please check your connection',
];
// Browser-extension noise, one list for both the message check and the
// stack-frame check (same dedup rationale as SENTRY_NETWORK_NOISE above).
const EXTENSION_URL_SCHEMES = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
];
const EXTENSION_ERROR_SIGNATURES = [
  'MetaMask',
  // WebExtension-only APIs / lifecycle errors: these can only come from an
  // extension content script whose promise rejection bubbled to our
  // onunhandledrejection, often with no stack for the scheme check to catch
  // (HOK-OS-4T: "Invalid call to runtime.sendMessage(). Tab not found.")
  'runtime.sendMessage',
  'Extension context invalidated',
  ...EXTENSION_URL_SCHEMES,
];

function isFilterableNetworkError(message?: string | null, name?: string | null): boolean {
  const msg = message ?? '';
  // A stale-chunk error reaching Sentry means retryImport's auto-reload guard
  // gave up — keep those visible (HOK-OS-4W) even though Chrome's variant
  // contains 'Failed to fetch'.
  if (isStaleChunkError(msg)) return false;
  if (name === 'FunctionsFetchError') return true;
  if (SENTRY_NETWORK_NOISE.some((sig) => msg.includes(sig))) return true;
  return isTransientNetworkError({ message: msg, name: name ?? undefined });
}

// Try to initialize Sentry, but don't let it break the app if blocked.
// Only report from a PRODUCTION BUILD connected to the PRODUCTION Supabase
// project. Every event is tagged "production", so both gates matter: a local
// `vite` dev server pointed at production data used to file real-looking
// production regressions (e.g. Vite's mid-session dependency re-optimisation
// loading a second copy of React), and a production build against staging
// would page the production alert rule for staging data.
let sentryInitialized = false;
if (SENTRY_DSN && import.meta.env.PROD && isProductionSupabase()) {
  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: 'production',
      integrations: [
        Sentry.browserTracingIntegration(),
        // Mask all text and block media in Session Replay. This app renders
        // member PII (names, DOB, emails, WhatsApp numbers, emergency contacts,
        // waiver signatures, payment amounts) and signed-URL photos; replays are
        // shipped to a third party (Sentry) and cannot be purged by member
        // anonymization/deletion, so they must never capture that content.
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tunnel: '/api/tunnel',
      
      // Performance Monitoring
      tracesSampleRate: 1.0,
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      
      // Handle ad blocker interference gracefully by catching network errors
      beforeSend: (event, hint) => {
        // Filter out Supabase auth auto-refresh errors (stale session / network failure)
        if (event.exception?.values?.some(v =>
          (v.type === 'AuthApiError' && v.value?.includes('Refresh Token')) ||
          v.type === 'AuthRetryableFetchError' ||
          v.type === 'AuthSessionMissingError'
        )) {
          return null;
        }

        // Filter out browser extension errors (MetaMask, etc.)
        if (event.exception) {
          const errorMessage = event.exception.values?.[0]?.value || '';
          const errorStack = event.exception.values?.[0]?.stacktrace?.frames || [];
          
          // Check for MetaMask and other extension errors
          if (EXTENSION_ERROR_SIGNATURES.some(sig => errorMessage.includes(sig)) ||
              errorStack.some(frame =>
                EXTENSION_URL_SCHEMES.some(scheme => frame.filename?.includes(scheme))
              )) {
            // Silently skip browser extension errors
            return null; // Don't send the event
          }

          // CSP-refused eval from an injected automation script (HOK-OS-8F)
          if (event.exception.values?.some(isInjectedEvalError)) {
            return null;
          }
        }

        // Check if this is a network error from ad blocker interference or transient failure
        // Check hint.originalException first (works when errors aren't re-wrapped)
        if (hint.originalException instanceof Error &&
            isFilterableNetworkError(hint.originalException.message, hint.originalException.name)) {
          return null;
        }

        // Also check the event's exception values for network errors
        // This catches re-wrapped errors where hint.originalException is a new Error()
        if (event.exception?.values) {
          const hasNetworkError = event.exception.values.some(exValue => {
            const msg = exValue.value || '';
            // Keep gave-up stale-chunk errors visible here too — Chrome's
            // "TypeError: Failed to fetch dynamically imported module" would
            // otherwise match the generic TypeError+fetch branch below.
            if (isStaleChunkError(msg)) return false;
            return isFilterableNetworkError(msg, exValue.type) ||
                   // Generic Safari/Chrome TypeError wrapping a fetch failure.
                   (exValue.type === 'TypeError' && msg.includes('fetch'));
          });
          if (hasNetworkError) {
            return null;
          }
        }

        // Filter Sentry.captureMessage events from logger.warn that are
        // tagged as transient network/database noise (no exception attached).
        if (!event.exception && typeof event.message === 'string') {
          const msg = event.message;
          if (msg.includes('Transient network error') ||
              msg.includes('Transient error during') ||
              msg.includes('Transient error in')) {
            return null;
          }

          // logger.warn(message, { error: err }) routes through captureMessage,
          // which hides the original Error in event.extra.error. Inspect there
          // for the same transient-network signatures we filter on exceptions.
          // Callers that collect failures also pass `errors: string[]` (e.g. the
          // photo-cleanup pass) — those strings embed the original message, so
          // match them the same way. A cleanup failure from a real DB error (RLS
          // denial, constraint violation) carries different text and still sends.
          const extra = event.extra as
            | { error?: { message?: string; name?: string }; errors?: unknown }
            | undefined;
          const extraError = extra?.error;
          // Suppress only when EVERY collected error is network noise — a batch
          // mixing a fetch failure with a real DB error must still be reported.
          const extraErrors = Array.isArray(extra?.errors) ? extra.errors : [];
          const allErrorsAreNoise =
            extraErrors.length > 0 &&
            extraErrors.every((e) => typeof e === 'string' && isFilterableNetworkError(e, null));
          if (isFilterableNetworkError(extraError?.message, extraError?.name) || allErrorsAreNoise) {
            return null;
          }
        }

        return event;
      }
    });
    sentryInitialized = true;
  } catch {
    // Silently handle initialization errors (e.g., ad blockers)
  }
}

// Wrap App with Sentry error boundary only if Sentry is available
const AppWithErrorBoundary = sentryInitialized 
  ? Sentry.withErrorBoundary(App, {
      fallback: ({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
            <h1 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. Our team has been notified.
            </p>
            <details className="mb-4">
              <summary className="cursor-pointer text-sm text-gray-500">Error details</summary>
              <pre className="mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto">
                {error instanceof Error ? error.message : 'Unknown error'}
              </pre>
            </details>
            <Button onClick={resetError} className="w-full">
              Try again
            </Button>
          </div>
        </div>
      ),
      beforeCapture: (scope) => {
        scope.setTag("errorBoundary", true);
        scope.setLevel("error");
        scope.setContext("errorBoundary", {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        });
      },
    })
  : App; // Use App directly if Sentry is not available

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppWithErrorBoundary />
  </StrictMode>
);

// Force redeployment - Fix module loading issues - 2025-07-09 21:37:30
